import React, { memo, useEffect, useRef } from 'react';
import { generateId } from './performanceUtils';

interface PerformanceOptions {
  /**
   * Whether to log render times to the console
   */
  logRenderTime?: boolean;
  
  /**
   * Threshold in milliseconds for warning about slow renders
   */
  slowRenderThreshold?: number;
  
  /**
   * Whether to memoize the component
   */
  memoize?: boolean;
  
  /**
   * Custom comparison function for memoization
   */
  arePropsEqual?: (prevProps: any, nextProps: any) => boolean;
}

/**
 * Higher Order Component that adds performance optimizations
 * @param Component The component to optimize
 * @param displayName Custom display name for the component
 * @param options Performance optimization options
 * @returns Optimized component
 */
export function withPerformanceOptimization<P extends object>(
  Component: React.ComponentType<P>,
  displayName?: string,
  options: PerformanceOptions = {}
): React.ComponentType<P> {
  const {
    logRenderTime = process.env.NODE_ENV === 'development',
    slowRenderThreshold = 16, // 16ms = 60fps
    memoize: shouldMemoize = true,
    arePropsEqual,
  } = options;
  
  // Component that measures render time
  const MeasuredComponent: React.FC<P> = (props) => {
    const componentName = displayName || Component.displayName || Component.name || 'Component';
    const renderCount = useRef(0);
    const id = useRef(generateId()).current;
    
    useEffect(() => {
      if (!logRenderTime) return;
      
      const startTime = performance.now();
      
      return () => {
        const endTime = performance.now();
        const renderTime = endTime - startTime;
        renderCount.current += 1;
        
        console.log(
          `[Render] ${componentName} #${renderCount.current} took ${Math.round(renderTime)}ms`
        );
        
        if (renderTime > slowRenderThreshold) {
          console.warn(
            `[Performance Warning] ${componentName} render #${renderCount.current} took ${Math.round(renderTime)}ms, which exceeds the threshold of ${slowRenderThreshold}ms`
          );
        }
      };
    });
    
    return <Component {...props} data-component-id={id} />;
  };
  
  // Set display name for developer tools
  MeasuredComponent.displayName = `withPerformanceOptimization(${displayName || Component.displayName || Component.name || 'Component'})`;
  
  // Apply memoization if requested
  if (shouldMemoize) {
    return memo(MeasuredComponent, arePropsEqual);
  }
  
  return MeasuredComponent;
}

/**
 * Extended version of React.memo with improved naming and comparison
 * @param Component The component to memoize
 * @param arePropsEqual Optional custom comparison function
 * @returns Memoized component
 */
export function memoWithName<P extends object>(
  Component: React.ComponentType<P>,
  arePropsEqual?: (prevProps: P, nextProps: P) => boolean
): React.MemoExoticComponent<React.ComponentType<P>> {
  const MemoComponent = memo(Component, arePropsEqual);
  
  // Preserve display name for dev tools
  MemoComponent.displayName = `Memo(${Component.displayName || Component.name || 'Component'})`;
  
  return MemoComponent;
}

/**
 * Creates a dependency array filter function that ignores certain keys
 * @param ignoredKeys Array of keys to ignore when comparing
 * @returns Function that can be used for React.memo's second argument
 */
export function createPropsFilter<P extends object>(
  ignoredKeys: Array<keyof P>
): (prevProps: P, nextProps: P) => boolean {
  return (prevProps: P, nextProps: P): boolean => {
    const prevEntries = Object.entries(prevProps);
    const nextEntries = Object.entries(nextProps);
    
    if (prevEntries.length !== nextEntries.length) {
      return false;
    }
    
    return prevEntries.every(([key, value]) => {
      // Skip comparison for ignored keys
      if (ignoredKeys.includes(key as keyof P)) {
        return true;
      }
      
      return value === (nextProps as any)[key];
    });
  };
}
